import ListIcon from '@mui/icons-material/List'
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, Paper, TextField } from '@mui/material'
import { FormAlert, FormHeader } from 'components'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'

import { ajvResolver } from '@hookform/resolvers/ajv'
import ModuleSchema, { IModuleFormData } from 'schema/security/module'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const ModuleForm: React.FC = () => {
  const [mainError, setMainError] = useState('')

  const params = useParams<IRouteParams>()
  const firstInputElement = useRef<any>(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm<IModuleFormData>({
    resolver: ajvResolver(ModuleSchema),
    defaultValues: {
      name: '',
      disabled: false,
    },
  })

  // main data load

  useEffect(() => {
    async function loadData() {
      const { id } = params

      await api
        .get(`/modules/${id}`)
        .then((response) => {
          const { data } = response.data

          const moduleResult = {
            id: data.id,
            name: data.name,
            disabled: data.disabled,
          }

          return moduleResult
        })
        .then((moduleResult: IModuleFormData) => {
          reset(moduleResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
    }
  }, [params, params.id])

  // data save

  const onSubmit: SubmitHandler<IModuleFormData> = useCallback(async (data) => {
    const payLoad: IModuleFormData = {
      id: '',
      name: data.name,
      disabled: data.disabled,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/modules`, payLoad)
        .then(history.push('/modules'))
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    } else {
      await api
        .post('/modules', payLoad)
        .then(history.push('/modules/new'))
        .then(() => reset())
        .then(() =>
          setTimeout(() => {
            firstInputElement.current.focus()
          }, 0)
        )
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    }
  }, [])

  const handleChange = (formField: any) => {
    setMainError('')
  }

  return (
    <Paper
      elevation={3}
      className={classes.paper}
    >
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid='form'
      >
        <FormHeader
          title='Módulos'
          icon={ListIcon}
          backRoute='/modules'
          showSaveButton={true}
          helpText='Nesta opção serão informados os módulos da Aplicação.'
        />

        <FormAlert
          setMainError={setMainError}
          mainError={mainError}
        />

        <Grid
          container
          spacing={1}
          className={mainError === '' ? classes.formContainer : classes.formContainerWithError}
        >
          <Grid
            item
            xs={12}
          >
            <TextField
              id='name'
              label='Nome'
              error={!!errors.name}
              helperText={errors?.name?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              autoFocus
              inputRef={firstInputElement}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 60,
              }}
              {...register('name', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <FormGroup className={classes.checkBox}>
              <Controller
                name='disabled'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                    }
                    label='Inativo'
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

export default ModuleForm
